/** Sign-up and onboarding option lists (mockups/auth/register.html, onboarding.html). */
import type { IconName } from "./icons";

/** Backend `Industry` (app/core/constants/industries.py). Templates map to these as DATA. */
export const INDUSTRIES = ["education", "healthcare", "real_estate", "hospitality", "human_resources", "government", "ecommerce", "business", "other"] as const;
export type IndustryKey = (typeof INDUSTRIES)[number];

export const INDUSTRY_ICONS: Readonly<Record<IndustryKey, IconName>> = {
  education: "graduation-cap",
  healthcare: "activity",
  real_estate: "home",
  hospitality: "star",
  human_resources: "users",
  government: "building",
  ecommerce: "package",
  business: "layers",
  other: "more-horizontal",
};

/** Older stored values (sign-up `org_type`, template `industry`) → canonical industry. */
const LEGACY_INDUSTRIES: Readonly<Record<string, IndustryKey>> = { school: "education", clinic: "healthcare", retail: "ecommerce", general: "business" };

export function canonicalIndustry(raw: string | null | undefined): IndustryKey | null {
  const key = (raw ?? "").trim().toLowerCase();
  if (!key) return null;
  return LEGACY_INDUSTRIES[key] ?? INDUSTRIES.find((industry) => industry === key) ?? null;
}

/** Backend `SetupGoal`; labels/effects come from `GET /v1/setup-goals` (template data). */
export const SETUP_GOALS = ["visitor_qa", "lead_capture", "booking", "customer_support", "internal_help_desk", "members_only", "announcements"] as const;
export type SetupGoalKey = (typeof SETUP_GOALS)[number];

export const SETUP_DESCRIPTION_MAX_LENGTH = 1_000;
export const INDUSTRY_LABEL_MAX_LENGTH = 80;
export const SETUP_STARTER_COUNT = 4;
export const SETUP_MAX_LEAD_STAGES = 8;
export const SETUP_MIN_LEAD_STAGES = 3;

/** Size bands sent as `size` at sign-up. */
export const ORG_SIZES = ["lt_200", "200_500", "501_1500", "1501_5000", "gt_5000"] as const;
export type OrgSizeKey = (typeof ORG_SIZES)[number];
export const DEFAULT_ORG_SIZE: OrgSizeKey = "lt_200";

/** Home market (DEC-042): bills in PHP, listed first in pickers, offers Filipino. */
export const HOME_COUNTRY = "PH";
/** `GET /v1/meta/countries` is static reference data: fetched once, kept for a day. */
export const COUNTRIES_GC_TIME_MS = 24 * 60 * 60 * 1_000;

/** Template card icons (`industry_templates.key` → icon) when the API sends none. */
export const TEMPLATE_ICONS: Readonly<Record<string, IconName>> = {
  school_admissions: "graduation-cap",
  student_help_desk: "building-school",
  clinic_front_desk: "activity",
  real_estate_leads: "home",
  hospitality_concierge: "star",
  hr_policy_assistant: "users",
  government_services: "building",
  ecommerce_support: "package",
  website_assistant: "globe",
  blank: "plus",
};

export const ONBOARDING_STEPS = ["organization", "knowledge", "configure", "test", "live"] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

export const CRAWL_PAGE_LIMITS = [25, 50, 100, 250] as const;
